import type { RefObject } from 'react'
import { useEffect, useState } from 'react'

function getVerticalPadding(element: HTMLElement): number {
  const styles = window.getComputedStyle(element)
  const paddingTop = Number.parseFloat(styles.paddingTop || '0') || 0
  const paddingBottom = Number.parseFloat(styles.paddingBottom || '0') || 0
  return paddingTop + paddingBottom
}

interface UseComposerBudgetParams {
  /** Fixed context chrome to reserve ahead of the split — header row + gap when visible. Does
   *  NOT include the context list's own height; that's part of what the budget gets split into. */
  contextFixedReserve: number
  inputWrapperRef: RefObject<HTMLElement | null>
  actionBarRef: RefObject<HTMLElement | null>
  maxComposerHeight: number
  layoutRevision: string
}

/**
 * Measures how much vertical space is left for the context list + input editor combined, once
 * the composer's fixed chrome (context header, action bar, input padding) is reserved. This is
 * the `budget` that allocateComposerHeights (composerDrag.ts) splits between the two regions.
 */
export function useComposerBudget({
  contextFixedReserve,
  inputWrapperRef,
  actionBarRef,
  maxComposerHeight,
  layoutRevision,
}: UseComposerBudgetParams): number {
  const [budget, setBudget] = useState(Number.POSITIVE_INFINITY)

  useEffect(() => {
    if (!Number.isFinite(maxComposerHeight)) {
      setBudget(Number.POSITIVE_INFINITY)
      return
    }

    const measure = () => {
      const inputWrapper = inputWrapperRef.current
      const actionBar = actionBarRef.current
      if (!inputWrapper || !actionBar) {
        return
      }

      const fixedChrome =
        contextFixedReserve + actionBar.offsetHeight + getVerticalPadding(inputWrapper)
      const nextBudget = Math.max(0, maxComposerHeight - fixedChrome)
      setBudget((current) => (current === nextBudget ? current : nextBudget))
    }

    measure()

    if (typeof ResizeObserver === 'undefined') {
      return
    }

    const observer = new ResizeObserver(measure)
    for (const element of [inputWrapperRef.current, actionBarRef.current]) {
      if (element) {
        observer.observe(element)
      }
    }

    return () => {
      observer.disconnect()
    }
  }, [actionBarRef, contextFixedReserve, inputWrapperRef, layoutRevision, maxComposerHeight])

  return budget
}
